import type { ElementType } from '@xshop/shared';
import type { ElementComponent } from '../render/elementRenderContext';
import { CheckboxElement, SelectElement } from './ChoiceFields';
import { FileUploadElement } from './FileUploadField';
import { FormElement } from './FormElement';
import { LabelElement } from './LabelElement';
import { StarsElement } from './StarsField';
import { InputElement, TextareaElement } from './TextFields';

// The eight types a visitor answers, and the components that draw them. The render layer dispatches on
// `type` through this one map, so adding a form element is a file in this folder and a line here —
// never a branch somewhere else in the renderer. All eight are the merchant's own vocabulary, gathered
// by a `form`.

/** The eight types this folder answers for. The renderer routes on exactly this set and no other. */
export const FORM_TYPES = [
  'input', 'textarea', 'select', 'checkbox', 'label', 'stars', 'file-upload', 'form'
] as const;

export type FormType = (typeof FORM_TYPES)[number];

/** Every form type and the component that draws it; the key set is closed, so it cannot fall short. */
export const FORM_ELEMENTS: Readonly<Record<FormType, ElementComponent>> = {
  input: InputElement,
  textarea: TextareaElement,
  select: SelectElement,
  checkbox: CheckboxElement,
  label: LabelElement,
  stars: StarsElement,
  'file-upload': FileUploadElement,
  form: FormElement
};

/** The same map in the shape `ElementTreeView` takes for its `renderers` prop. */
export const FORM_RENDERERS: Readonly<Partial<Record<ElementType, ElementComponent>>> = FORM_ELEMENTS;

// Reports whether a node type is drawn by this folder, so a check can assert the map is complete.
export function isFormType(type: string): type is FormType {
  return (FORM_TYPES as readonly string[]).includes(type);
}

export { FormScope, useFormAnswers, useEnclosingForm, useFormOrIsland, fieldIdOf, fieldTitle } from './formScope';
export type { FieldRule, FieldShape, FieldValue, FormAnswers, FormStatus } from './formScope';
export { checkField, isAnswered, validateFields } from './formValidation';
export { useFormField, fieldName, fieldRuleOf, type BoundField } from './useFormField';
export { FieldFrame, controlAttrs, useFieldBox, type ControlAttrs, type FieldBox } from './FieldFrame';
export { StarRating, starCount, starScore } from './StarRating';
export { PhotoPicker, UPLOAD_HINT, attachmentLimit } from './PhotoPicker';
export { FORM_SUBMIT_EVENT, type FormSubmitDetail } from './FormElement';
